import React, { MouseEvent, useEffect } from "react";

interface HouseRulesModalProps {
  isOpen: boolean;
  requiresAcceptance: boolean;
  isAccepting: boolean;
  error: string;
  onAccept: () => void;
  onClose: () => void;
}

const HouseRulesModal = ({
  isOpen,
  requiresAcceptance,
  isAccepting,
  error,
  onAccept,
  onClose,
}: HouseRulesModalProps) => {
  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !requiresAcceptance) onClose();
    };

    document.body.classList.add("rules-modal-open");
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.classList.remove("rules-modal-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen, onClose, requiresAcceptance]);

  const closeFromBackdrop = (event: MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget && !requiresAcceptance) onClose();
  };

  return (
    <aside
      className={`rules-modal ${isOpen ? "is-open" : ""}`}
      id="rulesModal"
      aria-modal="true"
      role="dialog"
      aria-labelledby="rules-modal-title"
      hidden={!isOpen}
      onClick={closeFromBackdrop}
    >
      <div className="rules-modal-card">
        <div className="rules-modal-icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="32" height="32">
            <path
              d="M16 4 L26 7 V15 c0 6 -4 11 -10 13 -6 -2 -10 -7 -10 -13 V7 z"
              fill="#F5B041"
              fillOpacity=".22"
              stroke="#C4841D"
              strokeWidth="2.2"
              strokeLinejoin="round"
            />
            <circle cx="13" cy="14" r="1.4" fill="#C4841D" />
            <circle cx="19" cy="14" r="1.4" fill="#C4841D" />
            <path
              d="M11.5 18.5 c 1.5 2 3 3 4.5 3 s 3 -1 4.5 -3"
              fill="none"
              stroke="#C4841D"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="rules-modal-eyebrow">Welcome to the community</div>
        <h2 id="rules-modal-title" className="rules-modal-title">
          House rules. <em>Four of them.</em>
        </h2>
        <p className="rules-modal-intro">
          A small set of guidelines that exists for one reason: to keep this
          place worth coming back to.
        </p>

        <div className="rule-list">
          <div className="rule">
            <span className="rule-num" aria-hidden="true">
              1
            </span>
            <div className="rule-text">
              <div className="rule-title">
                Be useful, be curious, be generous.
              </div>
              <div className="rule-body">
                Ideas, questions, counter-arguments, genuinely good jokes.{" "}
                <em>
                  All three in one post gets you a 42 Ultimate Answer Badge.
                </em>
              </div>
            </div>
          </div>

          <div className="rule">
            <span className="rule-num" aria-hidden="true">
              2
            </span>
            <div className="rule-text">
              <div className="rule-title">Be kind.</div>
              <div className="rule-body">
                Disagree freely, but build on ideas rather than flatten them.{" "}
                <em>
                  Be the first kind, the second have plenty of platforms
                  already.
                </em>
              </div>
            </div>
          </div>

          <div className="rule">
            <span className="rule-num" aria-hidden="true">
              3
            </span>
            <div className="rule-text">
              <div className="rule-title">
                No selling, no spam, no LinkedIn cosplay.
              </div>
              <div className="rule-body">
                Contribute to the conversation, don't hijack it.{" "}
                <em>If you're not sure, you're probably hijacking it.</em>
              </div>
            </div>
          </div>

          <div className="rule">
            <span className="rule-num" aria-hidden="true">
              4
            </span>
            <div className="rule-text">
              <div className="rule-title">
                Bigotry, harassment, and cruelty get you out.
              </div>
              <div className="rule-body">
                No warnings.{" "}
                <em>Life's too short and the membership's too good.</em>
              </div>
            </div>
          </div>
        </div>

        {requiresAcceptance && (
          <div className="rules-pledge">
            <p className="rules-pledge-text">
              I, the undersigned thinker,
              <br />
              do solemnly <em>pinky swear</em> to abide by these rules.
            </p>
            <button
              type="button"
              className="rules-accept-btn"
              id="rulesAcceptBtn"
              disabled={isAccepting}
              onClick={onAccept}
            >
              {isAccepting ? "Accepting..." : "I accept →"}
            </button>
            {error && <p role="alert">{error}</p>}
          </div>
        )}

        <div className="rules-modal-footer">
          <p>
            If anything ever feels off, drop us a line at{" "}
            <a href="mailto:groundcontrol@42courses.com">
              groundcontrol@42courses.com
            </a>
            .
          </p>
          <p>
            <strong>Otherwise, dive in. We're glad you're here.</strong>
          </p>
        </div>

        {!requiresAcceptance && (
          <button
            type="button"
            className="rules-modal-close"
            id="rulesCloseBtn"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        )}
      </div>
    </aside>
  );
};

export default HouseRulesModal;
